import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { Reveal } from "./Reveal";
import PeekInside from "./PeekInside";
import { BOOKS } from "../data/books";

const BookCard = ({ book, index, onPeek }) => (
  <motion.article
    data-testid={`book-card-${book.id}`}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (index % 3) * 0.15, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ rotate: index % 2 === 0 ? -1.5 : 1.5, y: -8 }}
    className="group flex flex-col overflow-hidden rounded-2xl border-2 border-ink bg-white shadow-pop transition-shadow duration-300 hover:shadow-pop-lg"
  >
    <div
      className="halftone relative flex h-72 items-center justify-center overflow-hidden border-b-2 border-ink"
      style={{ background: `radial-gradient(circle at 50% 30%, ${book.color}26, transparent 70%), #FFFDF6` }}
    >
      <span className="absolute left-4 top-4 font-mono text-5xl font-bold text-ink/10">{book.num}</span>
      <img
        src={book.cover}
        alt={`${book.title}: ${book.subtitle} — book cover`}
        className="h-[92%] w-auto rounded-lg border-2 border-ink object-contain shadow-pop transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:rotate-1"
        loading="lazy"
      />
    </div>

    <div className="flex flex-1 flex-col p-6">
      <span
        className="sticker w-fit -rotate-1 rounded-full text-[10px] sm:text-xs"
        style={{ backgroundColor: book.color, color: "#FFFDF6" }}
      >
        {book.badge}
      </span>
      <h3 className="mt-4 font-head text-xl font-black tracking-tight sm:text-2xl">
        {book.title}
        <span className="mt-1 block font-display text-base font-semibold text-ink/60">{book.subtitle}</span>
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/70 sm:text-base">{book.tagline}</p>
      <button
        data-testid={`peek-btn-${book.id}`}
        onClick={() => onPeek(book)}
        className="mt-5 flex items-center justify-center gap-2 rounded-full border-2 border-ink bg-white px-5 py-2.5 font-head text-sm font-extrabold uppercase tracking-wide shadow-pop-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-cream hover:shadow-pop"
      >
        <BookOpen className="h-4 w-4" /> Peek inside
      </button>
      <a
        data-testid={`amazon-buy-btn-${book.id}`}
        href={book.amazon}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex items-center justify-center gap-2 rounded-full border-2 border-ink bg-banana px-5 py-3 font-head text-sm font-extrabold uppercase tracking-wide shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm"
      >
        Buy on Amazon
        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </div>
  </motion.article>
);

export default function Books() {
  const [peekBook, setPeekBook] = useState(null);

  return (
    <section id="books" data-testid="books-section" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <span className="sticker rotate-1 rounded-lg bg-white">The series</span>
          <h2 className="mt-5 max-w-2xl font-head text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Five adventures. <span className="text-maroon">One big message:</span>{" "}
            <span className="text-royal">you are enough.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {BOOKS.map((b, i) => (
            <BookCard key={b.id} book={b} index={i} onPeek={setPeekBook} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {peekBook && <PeekInside book={peekBook} onClose={() => setPeekBook(null)} />}
      </AnimatePresence>
    </section>
  );
}
